// Cast auto-matching for the RP section — the single source of truth for
// "which roster characters does this text mention", shared by mid-story
// auto-casting (player text), AI-reply arrival detection, and prompt-driven
// opening-scene seeding. Matching is case-insensitive and word-bounded.
//
// The ambiguity rule exists because of a live incident (2026-07-30): a bare
// "Haley" in generated prose seeded THREE Haley roster variants into a
// story's cast at once. A first name shared by several of the candidate
// characters identifies none of them, so it matches none of them. Callers
// that pre-filter the candidate list (blocked characters, already-cast
// characters) get the emergent right behavior: once only one variant is
// eligible, the name becomes unambiguous again.

export const firstNameOf = (name) => String(name || '').trim().split(/\s+/)[0] || '';

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export function matchCharactersInText(text, chars, opts = {}) {
  const t = String(text || '');
  if (!t) return [];
  const exclude = new Set(opts.excludeIds || []);
  const excludeFirst = new Set((opts.excludeFirstNames || []).map(s => String(s || '').toLowerCase()));
  const firstCounts = new Map();
  for (const c of chars || []) {
    if (!c?.id || !c?.name) continue;
    const f = firstNameOf(c.name).toLowerCase();
    if (f.length < 3) continue;
    firstCounts.set(f, (firstCounts.get(f) || 0) + 1);
  }
  const out = [];
  const candidates = (chars || []).filter(c => c?.id && c?.name && !exclude.has(c.id));
  for (const c of candidates) {
    const first = firstNameOf(c.name);
    const lower = first.toLowerCase();
    if (first.length < 3 || excludeFirst.has(lower)) continue;
    if ((firstCounts.get(lower) || 0) > 1) continue; // shared name identifies nobody
    let m = new RegExp(`\\b${escapeRe(first)}\\b`, 'i').exec(t);
    // NICKNAME PREFIXES (Haley live, 2026-08-12: she wrote "too, Seb. You
    // know that" with Sebastian & Kaleb pinned — "Seb" matched nothing, so
    // only Kaleb was treated as named and only Kaleb replied). The players
    // write "Seb", "Nol", "Kal": an UNAMBIGUOUS prefix of exactly one
    // candidate's first name, 3+ letters, counts as that name. Same ambiguity
    // discipline as full names — a prefix that fits several candidates (or
    // equals another candidate's full first name) identifies nobody.
    if (!m) {
      for (let len = first.length - 1; len >= 3 && !m; len--) {
        const prefix = first.slice(0, len);
        const plc = prefix.toLowerCase();
        const fitsOthers = candidates.some(o => o !== c &&
          firstNameOf(o.name).toLowerCase().startsWith(plc));
        if (fitsOthers || excludeFirst.has(plc)) break; // shorter prefixes only get MORE ambiguous
        m = new RegExp(`\\b${escapeRe(prefix)}\\b`, 'i').exec(t);
      }
    }
    if (m) out.push({ id: c.id, first, index: m.index });
  }
  out.sort((a, b) => a.index - b.index);
  return typeof opts.max === 'number' ? out.slice(0, opts.max) : out;
}

export function detectProtagonist(promptText, chars) {
  const t = String(promptText || '');
  if (!t) return null;
  const matches = matchCharactersInText(t, chars);
  if (!matches.length) return null;
  const explicit = /\b(?:playing|play as|as|i am|i'm)\s+([A-Z][a-zA-Z]{2,30})/i.exec(t);
  if (explicit) {
    const hit = matches.find(m => m.first.toLowerCase() === explicit[1].toLowerCase());
    if (hit) return hit;
  }
  const lead = matches[0];
  return lead.index <= 2 ? lead : null;
}
